import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  SIDEKICK_FEATURE_EVENT,
  type SidekickSuggestionPlacement,
  useSidekickFeatureEventReporter,
} from '../../featureEvents';
import { useSidekickConsentGate } from '../../hooks/useSidekickConsentGate';
import {
  CONSENT_STATUS,
  selectIsConsentGranted,
} from '../../state/aiSidekickConsentSlice';
import { expandDock, openPanel, selectChip } from '../../state/aiSidekickSlice';
import type { StateWithSidekickChat } from '../../state/chatTypes';
import { dispatchHostUserMessage } from '../../state/thunks';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import { showSidekickErrorToast } from '../toast';

/** Consent-aware entry handlers for the dock's bar, chips, and composer. */
export type DockConsentEntry = {
  /**
   * Handles a minimized-bar press: opens the drawer for a consented user
   * and expands the welcome card for everyone else.
   */
  handleBarClick: () => void;
  /**
   * Handles a suggestion chip press, carrying its resolved label as the
   * turn and reporting where the chip sat once the turn is sent.
   */
  handleChipClick: (
    chipId: string,
    label: string,
    placement: SidekickSuggestionPlacement
  ) => void;
  /** Handles a composer submission. */
  handleSubmit: (value: string) => void;
  /** True while a consent status check is in flight. */
  isConsentCheckLoading: boolean;
};

/**
 * Routes the dock's entry surfaces through the legal-disclaimer gate
 * before any turn reaches the backend.
 *
 * The dock has no transcript, so every surface hands off to the drawer.
 * That handoff has to wait on consent: queueing the host message up
 * front would leave it pending behind the disclaimer and send the moment
 * the gate cleared, which is exactly the turn the gate exists to
 * prevent. The message is therefore dispatched only from `onGranted`,
 * so a first-time user sees the modal with nothing queued.
 *
 * Presentation stays here rather than in `useSidekickConsentGate`,
 * matching the package convention that components own toasts.
 *
 * @returns The bar, chip, and composer handlers plus the in-flight check flag.
 */
export const useDockConsentEntry = (): DockConsentEntry => {
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const dispatch =
    useDispatch<ThunkDispatch<StateWithSidekickChat, unknown, AnyAction>>();
  const isConsentGranted = useSelector(selectIsConsentGranted);
  const { requestOpen, status } = useSidekickConsentGate();
  const { emitOnQuestionSent } = useSidekickFeatureEventReporter();

  const showConsentErrorToast = useCallback(() => {
    showSidekickErrorToast({
      message: t('chat.error.network.toast'),
      closeAriaLabel: t('chat.error.network.toastDismissAriaLabel'),
      testId: 'ai-sidekick-dock-error-toast',
    });
  }, [t]);

  const openWithConsent = useCallback(
    (onGranted: () => void) => {
      void requestOpen({ onGranted }).then((outcome) => {
        if (outcome === 'error') {
          showConsentErrorToast();
        }
      });
    },
    [requestOpen, showConsentErrorToast]
  );

  // The bar is a way in, not a submission: any answer short of a grant
  // falls back to the welcome card, which carries the disclaimer, so a
  // failed lookup needs no toast.
  const handleBarClick = useCallback(() => {
    void requestOpen({ openOnDenied: false }).then((outcome) => {
      if (outcome !== 'granted') {
        dispatch(expandDock());
      }
    });
  }, [dispatch, requestOpen]);

  const handleChipClick = useCallback(
    (chipId: string, label: string, placement: SidekickSuggestionPlacement) => {
      const sendChipQuestion = () =>
        emitOnQuestionSent(
          {
            type: SIDEKICK_FEATURE_EVENT.SUGGESTION_SELECTED,
            surface: 'dock',
            ...placement,
          },
          {
            question: label,
            submit: () => dispatch(dispatchHostUserMessage(label)),
          }
        );
      if (!isConsentGranted) {
        openWithConsent(() => {
          dispatch(selectChip(chipId));
          sendChipQuestion();
        });
        return;
      }
      dispatch(selectChip(chipId));
      dispatch(openPanel());
      sendChipQuestion();
    },
    [isConsentGranted, dispatch, emitOnQuestionSent, openWithConsent]
  );

  const handleSubmit = useCallback(
    (value: string) => {
      if (!isConsentGranted) {
        openWithConsent(() => {
          dispatch(dispatchHostUserMessage(value));
        });
        return;
      }
      dispatch(openPanel());
      dispatch(dispatchHostUserMessage(value));
    },
    [isConsentGranted, dispatch, openWithConsent]
  );

  return {
    handleBarClick,
    handleChipClick,
    handleSubmit,
    isConsentCheckLoading: status === CONSENT_STATUS.CHECKING,
  };
};
